import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import { useLayoutEffect, useRef, useState } from 'react'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  SPOTLIGHT_BADGE_CATEGORY_DESCRIPTIONS,
  SPOTLIGHT_BADGE_CATEGORY_LABELS,
  SpotlightBadgeCategory,
} from '@ivy-monorepo/shared'
import Icon from '../Icon'

type SpotlightBadgePriorityTableProps = {
  order: SpotlightBadgeCategory[]
  onChange: (order: SpotlightBadgeCategory[]) => void
}

type SortableRowProps = {
  category: SpotlightBadgeCategory
  position: number
}

const BadgePriorityRowContent = ({
  category,
  position,
  dragHandleProps,
  isOverlay = false,
}: SortableRowProps & {
  dragHandleProps?: Record<string, unknown>
  isOverlay?: boolean
}) => (
  <>
    <td className="map-filter-drag-cell">
      <button
        type="button"
        className="map-filter-drag-handle"
        aria-label={`Drag to reorder ${SPOTLIGHT_BADGE_CATEGORY_LABELS[category]}`}
        disabled={isOverlay}
        {...dragHandleProps}
      >
        <Icon name="GripVertical" className="map-filter-drag-icon" />
        <span className="map-filter-position-label">{position}</span>
      </button>
    </td>
    <td>
      <code>{category}</code>
    </td>
    <td>{SPOTLIGHT_BADGE_CATEGORY_LABELS[category]}</td>
    <td className="feature-flag-subtitle">{SPOTLIGHT_BADGE_CATEGORY_DESCRIPTIONS[category]}</td>
  </>
)

const SortableBadgePriorityRow = ({ category, position }: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: category,
  })

  return (
    <tr
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'map-filter-row-dragging' : undefined}
    >
      <BadgePriorityRowContent category={category} position={position} dragHandleProps={{ ...attributes, ...listeners }} />
    </tr>
  )
}

const SpotlightBadgePriorityTable = ({ order, onChange }: SpotlightBadgePriorityTableProps) => {
  const tableRef = useRef<HTMLTableElement>(null)
  const [tableWidth, setTableWidth] = useState<number>()
  const [activeKey, setActiveKey] = useState<SpotlightBadgeCategory | null>(null)

  useLayoutEffect(() => {
    if (!tableRef.current) {
      return
    }

    const updateWidth = () => setTableWidth(tableRef.current?.offsetWidth)
    updateWidth()

    const observer = new ResizeObserver(updateWidth)
    observer.observe(tableRef.current)
    return () => observer.disconnect()
  }, [order.length])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const activePosition = activeKey == null ? -1 : order.indexOf(activeKey)

  const handleDragStart = (event: DragStartEvent) => {
    setActiveKey(event.active.id as SpotlightBadgeCategory)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveKey(null)

    const { active, over } = event
    if (!over || active.id === over.id) {
      return
    }

    const oldIndex = order.indexOf(active.id as SpotlightBadgeCategory)
    const newIndex = order.indexOf(over.id as SpotlightBadgeCategory)
    if (oldIndex < 0 || newIndex < 0) {
      return
    }

    onChange(arrayMove(order, oldIndex, newIndex))
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveKey(null)}
    >
      <table ref={tableRef} className="map-filters-table spotlight-sort-table">
        <thead>
          <tr>
            <th className="map-filter-drag-header">Priority</th>
            <th>Key</th>
            <th>Label</th>
            <th>Wins the badge when...</th>
          </tr>
        </thead>
        <tbody>
          <SortableContext items={order} strategy={verticalListSortingStrategy}>
            {order.map((category, index) => (
              <SortableBadgePriorityRow key={category} category={category} position={index} />
            ))}
          </SortableContext>
        </tbody>
      </table>
      <DragOverlay dropAnimation={null}>
        {activeKey ? (
          <table
            className="map-filters-table map-filters-table-overlay"
            style={tableWidth ? { width: tableWidth } : undefined}
          >
            <tbody>
              <tr className="map-filter-row-overlay">
                <BadgePriorityRowContent category={activeKey} position={activePosition} isOverlay />
              </tr>
            </tbody>
          </table>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

export default SpotlightBadgePriorityTable
